import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { CalendarClock, Check, LoaderCircle, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { EASE } from "@/components/landing/Reveal";

const CATEGORIES = ["Préfecture", "Rendez-vous", "Examen", "OFII", "Autre"];
const daysUntil = (d) => Math.ceil((new Date(`${d}T00:00:00`) - new Date()) / 86400000);

function Chip({ date, done }) {
  if (done) return <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-[10px] font-bold text-emeraude">Fait</span>;
  const days = daysUntil(date);
  if (days < 0) return <span className="rounded-full bg-rose-50 px-2.5 py-1 text-[10px] font-bold text-rouge">Dépassée</span>;
  const cls = days <= 7 ? "bg-rose-50 text-rouge" : days <= 21 ? "bg-amber-50 text-ambre" : "bg-bleu-light text-bleu";
  return <span className={`rounded-full px-2.5 py-1 text-[10px] font-bold ${cls}`}>J-{days}</span>;
}

export default function MesEcheances() {
  const [items, setItems] = useState(null);
  const [form, setForm] = useState({ title: "", date: "", category: "Préfecture" });
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    api.get("/deadlines").then((r) => setItems(r.data)).catch(() => setItems(false));
  }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.date) return;
    setAdding(true);
    try {
      const { data } = await api.post("/deadlines", form);
      setItems((it) => [...it, data].sort((a, b) => a.date.localeCompare(b.date)));
      setForm({ title: "", date: "", category: "Préfecture" });
      toast.success("Échéance ajoutée.");
    } catch {
      toast.error("Impossible d'ajouter cette échéance.");
    } finally {
      setAdding(false);
    }
  };

  const toggleDone = async (item) => {
    setItems((it) => it.map((d) => (d.id === item.id ? { ...d, done: !d.done } : d)));
    try {
      await api.patch(`/deadlines/${item.id}`, { done: !item.done });
    } catch {
      setItems((it) => it.map((d) => (d.id === item.id ? { ...d, done: item.done } : d)));
      toast.error("Impossible de mettre à jour cette échéance.");
    }
  };

  const remove = async (item) => {
    try {
      await api.delete(`/deadlines/${item.id}`);
      setItems((it) => it.filter((d) => d.id !== item.id));
      toast.success("Échéance supprimée.");
    } catch {
      toast.error("Impossible de supprimer cette échéance.");
    }
  };

  if (items === null)
    return <div className="flex justify-center py-24"><LoaderCircle className="h-7 w-7 animate-spin text-bleu" /></div>;
  if (items === false)
    return <p className="py-24 text-center text-sm text-slate-500">Impossible de charger vos échéances.</p>;

  const pending = items.filter((d) => !d.done);
  const done = items.filter((d) => d.done);

  return (
    <div className="mx-auto max-w-3xl space-y-6" data-testid="page-mes-echeances">
      <div>
        <h1 className="font-display text-2xl font-extrabold text-slate-900 sm:text-3xl">Mes échéances</h1>
        <p className="mt-1 text-sm text-slate-500">Ne manquez plus aucune date importante : renouvellements, rendez-vous, examens.</p>
        <p className="mt-2 inline-flex rounded-full bg-bleu-light px-3.5 py-1.5 text-[11px] font-semibold text-bleu" data-testid="echeances-email-note">
          📬 Un rappel vous est aussi envoyé par email à J-7, J-3 et le jour J.
        </p>
      </div>

      <form onSubmit={add} className="card-integra grid gap-3 p-5 sm:grid-cols-[1fr_auto_auto_auto]" data-testid="deadlines-add-form">
        <input
          value={form.title}
          onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
          placeholder="Ex : Rendez-vous OFII"
          className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm placeholder:text-slate-400 focus:border-bleu focus:outline-none focus:ring-2 focus:ring-bleu/15"
          data-testid="deadline-title-input"
        />
        <input
          type="date"
          value={form.date}
          onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
          className="rounded-xl border border-slate-200 px-3 py-2.5 text-sm text-slate-600 focus:border-bleu focus:outline-none focus:ring-2 focus:ring-bleu/15"
          data-testid="deadline-date-input"
        />
        <select
          value={form.category}
          onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
          className="rounded-xl border border-slate-200 px-3 py-2.5 text-sm text-slate-600 focus:border-bleu focus:outline-none"
          data-testid="deadline-category-select"
        >
          {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
        </select>
        <button type="submit" disabled={adding || !form.title.trim() || !form.date} className="btn-primary !px-5 !py-2.5 disabled:opacity-50" data-testid="deadline-add-submit">
          <Plus className="h-4 w-4" /> Ajouter
        </button>
      </form>

      <section data-testid="deadlines-pending-list">
        <h2 className="mb-2.5 text-xs font-bold uppercase tracking-wider text-slate-400">À venir ({pending.length})</h2>
        {pending.length === 0 && <p className="card-integra p-5 text-sm text-slate-500">Aucune échéance en attente. Ajoutez vos dates importantes ci-dessus.</p>}
        <ul className="space-y-2">
          {pending.map((item, i) => (
            <motion.li
              key={item.id}
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, delay: i * 0.05, ease: EASE }}
              className="card-integra flex items-center gap-3.5 p-4"
              data-testid={`deadline-item-${item.id}`}
            >
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-bleu-light text-bleu"><CalendarClock className="h-4.5 w-4.5" /></span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-bold text-slate-900">{item.title}</p>
                <p className="text-[11px] text-slate-400">
                  {new Date(`${item.date}T00:00:00`).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" })} · {item.category}
                </p>
              </div>
              <Chip date={item.date} done={item.done} />
              <button onClick={() => toggleDone(item)} className="rounded-full border border-slate-200 p-2 text-slate-400 transition-colors hover:border-emeraude hover:text-emeraude" aria-label="Marquer comme fait" data-testid={`deadline-done-${item.id}`}>
                <Check className="h-3.5 w-3.5" />
              </button>
              <button onClick={() => remove(item)} className="text-slate-300 transition-colors hover:text-rouge" aria-label="Supprimer" data-testid={`deadline-delete-${item.id}`}>
                <Trash2 className="h-4 w-4" />
              </button>
            </motion.li>
          ))}
        </ul>
      </section>

      {done.length > 0 && (
        <section data-testid="deadlines-done-list">
          <h2 className="mb-2.5 text-xs font-bold uppercase tracking-wider text-slate-400">Terminées ({done.length})</h2>
          <ul className="space-y-2">
            {done.map((item) => (
              <li key={item.id} className="card-integra flex items-center gap-3.5 p-4 opacity-70">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-emeraude"><Check className="h-4.5 w-4.5" /></span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-slate-500 line-through">{item.title}</p>
                  <p className="text-[11px] text-slate-400">{new Date(`${item.date}T00:00:00`).toLocaleDateString("fr-FR")} · {item.category}</p>
                </div>
                <button onClick={() => toggleDone(item)} className="text-[11px] font-semibold text-bleu hover:underline" data-testid={`deadline-undo-${item.id}`}>Rouvrir</button>
                <button onClick={() => remove(item)} className="text-slate-300 transition-colors hover:text-rouge" aria-label="Supprimer" data-testid={`deadline-delete-done-${item.id}`}>
                  <Trash2 className="h-4 w-4" />
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
